import { useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import { useAuth } from "@/context/AuthContext";
import { ArrowRight, Loader2, PlayCircle } from "lucide-react";
import { toast } from "sonner";
import { BrandLink } from "@/components/Brand";

export default function LoginPage() {
  const { login } = useAuth();
  const nav = useNavigate();
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [busy, setBusy] = useState(false);

  const submit = async (e) => {
    e.preventDefault();
    setBusy(true);
    try {
      await login(email, password);
      toast.success("Welcome back");
      nav("/dashboard");
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Login failed");
    } finally { setBusy(false); }
  };

  return (
    <div className="min-h-screen grid md:grid-cols-2">
      <div className="hidden md:block relative overflow-hidden">
        <img src="https://images.unsplash.com/photo-1530046614490-89e6f776b83b?auto=format&fit=crop&w=1400&q=80" alt="Workshop" className="absolute inset-0 w-full h-full object-cover" />
        <div className="absolute inset-0 bg-black/65" />
        <div className="relative h-full p-12 flex flex-col justify-between text-white">
          <div className="bg-card rounded-lg p-3 w-fit"><BrandLink /></div>
          <div>
            <div className="label-overline text-white/60">For workshops</div>
            <h2 className="font-display text-4xl font-bold tracking-tight mt-3 leading-tight">
              Welcome back.<br />Inspect. <span className="text-primary">Advise.</span> Sell.
            </h2>
            <div className="mt-6 max-w-sm rounded-lg border border-primary/40 bg-black/40 backdrop-blur-sm p-4" data-testid="login-vv-promo">
              <div className="flex items-center gap-2">
                <PlayCircle className="w-4 h-4 text-primary" />
                <span className="text-[10px] font-bold uppercase tracking-[0.25em] text-primary">Featuring Vehicle Visuals&trade;</span>
              </div>
              <p className="text-sm text-white/85 mt-2 leading-relaxed">
                3D repair animations auto-attach to every quote line — your customers
                <span className="text-primary font-semibold"> see the repair before they approve it</span>.
                Customers approve what they understand.
              </p>
            </div>
          </div>
          <div className="font-mono-plex text-xs text-white/50">More transparency · More trust · More sales</div>
        </div>
      </div>

      <div className="flex items-center justify-center p-6 md:p-12 bg-background">
        <form onSubmit={submit} className="w-full max-w-md space-y-5" data-testid="login-form">
          <div>
            <div className="label-overline">Sign in</div>
            <h1 className="font-display text-3xl font-bold tracking-tight mt-2">Welcome back</h1>
          </div>
          <div className="space-y-1">
            <label className="text-sm font-medium">Email</label>
            <input type="email" required value={email} onChange={(e) => setEmail(e.target.value)} className="input-default" data-testid="login-email-input" />
          </div>
          <div className="space-y-1">
            <div className="flex items-center justify-between">
              <label className="text-sm font-medium">Password</label>
              <Link
                to="/forgot-password"
                className="text-xs text-muted-foreground hover:text-primary font-semibold"
                data-testid="login-forgot-password-link"
              >
                Forgot password?
              </Link>
            </div>
            <input type="password" required value={password} onChange={(e) => setPassword(e.target.value)} className="input-default" data-testid="login-password-input" />
          </div>
          <button type="submit" disabled={busy} className="btn-primary w-full" data-testid="login-submit-btn">
            {busy ? <Loader2 className="w-4 h-4 animate-spin" /> : <>Sign in <ArrowRight className="w-4 h-4" /></>}
          </button>
          <p className="text-sm text-muted-foreground">
            New here? <Link to="/register" className="text-primary font-semibold hover:underline" data-testid="login-to-register-link">Create an account</Link>
          </p>
        </form>
      </div>
    </div>
  );
}
